/* INP optimizations for Shopify (Testament-like themes)
   - passive listeners where safe
   - event delegation for cart drawer qty buttons
   - debounce cart updates to reduce long tasks
   - immediate UI feedback to improve INP
*/
(function () {
  // ---------- 1) Make some listeners passive by default (safe subset) ----------
  // This reduces main-thread blocking on touch/scroll interactions.
  const supportsPassive = (() => {
    let ok = false;
    try {
      const opts = Object.defineProperty({}, 'passive', { get() { ok = true; } });
      window.addEventListener('test', null, opts);
      window.removeEventListener('test', null, opts);
    } catch (e) {}
    return ok;
  })();

  if (supportsPassive) {
    const _add = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      // Only force passive on these events when options not explicitly set.
      const passiveTypes = new Set(['touchstart', 'touchmove', 'wheel', 'scroll']);
      if (passiveTypes.has(type) && (options === undefined || options === null)) {
        return _add.call(this, type, listener, { passive: true });
      }
      return _add.call(this, type, listener, options);
    };
  }

  // ---------- Helpers ----------
  const $ = (sel, root = document) => root.querySelector(sel);
  const closestAny = (el, selectors) => selectors.map(s => el.closest(s)).find(Boolean);

  // Identify cart drawer root (common ids/classes)
  function getDrawerRoot() {
    return (
      $('#CartDrawer') ||
      $('#AjaxCartDrawer') ||
      $('.cart-drawer') ||
      $('.drawer--cart') ||
      document
    );
  }

  // ---------- 2) Debounced cart change ----------
  let pendingChanges = new Map(); // line -> quantity
  let changeTimer = null;
  let isUpdating = false;

  function scheduleCartChange(line, quantity) {
    pendingChanges.set(String(line), Number(quantity));
    if (changeTimer) clearTimeout(changeTimer);

    // Debounce: adjust 250–400ms if needed
    changeTimer = setTimeout(flushCartChanges, 300);
  }

  async function flushCartChanges() {
    if (isUpdating || pendingChanges.size === 0) return;
    isUpdating = true;

    // Take snapshot and clear queue
    const entries = Array.from(pendingChanges.entries());
    pendingChanges.clear();

    try {
      // Apply sequentially to avoid theme conflicts (safe)
      for (const [line, quantity] of entries) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
        });
      }

      // If your theme has a built-in drawer refresh event, trigger it.
      // Many themes listen to 'cart:refresh' or re-render on 'cart:updated'.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      // Fail silently; theme will still work, user can refresh
      console.warn('INP cart change failed', e);
    } finally {
      isUpdating = false;
    }
  }

  // ---------- 3) Event delegation for qty buttons inside drawer ----------
  function initQtyDelegation() {
    const root = getDrawerRoot();

    document.addEventListener('click', (e) => {
      // Common selectors for plus/minus in Shopify drawers
      const btn = closestAny(e.target, [
        '[data-qty-plus]',
        '[data-qty-minus]',
        '.qty-plus',
        '.qty-minus',
        'button[name="plus"]',
        'button[name="minus"]',
        'button[data-quantity-plus]',
        'button[data-quantity-minus]',
        '.quantity__button'
      ]);
      if (!btn) return;

      // Must be inside drawer/cart area
      const inDrawer = btn.closest('#CartDrawer, #AjaxCartDrawer, .cart-drawer, .drawer--cart');
      if (!inDrawer) return;

      // Find the quantity input and cart line
      const item = btn.closest('[data-cart-item], .cart-item, .ajaxcart__row, [data-line]') || btn.parentElement;
      const qtyInput =
        item && (item.querySelector('input[name="updates[]"], input.quantity__input, input[data-quantity-input]') ||
        item.querySelector('input[type="number"]'));

      if (!qtyInput) return;

      // Determine line number (Shopify cart uses line)
      const lineAttr =
        (item && (item.getAttribute('data-line') || item.getAttribute('data-cart-item'))) ||
        (qtyInput.getAttribute('data-line')) ||
        (qtyInput.closest('[data-line]') && qtyInput.closest('[data-line]').getAttribute('data-line'));

      const line = Number(lineAttr);
      if (!line || Number.isNaN(line)) return;

      // Immediate UI feedback (improves INP)
      const current = Number(qtyInput.value || 0);
      let next = current;

      const isMinus =
        btn.matches('[data-qty-minus], .qty-minus, button[name="minus"], button[data-quantity-minus]') ||
        (btn.getAttribute('aria-label') || '').toLowerCase().includes('minus');

      const isPlus =
        btn.matches('[data-qty-plus], .qty-plus, button[name="plus"], button[data-quantity-plus]') ||
        (btn.getAttribute('aria-label') || '').toLowerCase().includes('plus');

      // Some themes use a single .quantity__button; infer via svg/position
      if (!isMinus && !isPlus) {
        // fallback: if button is first, treat as minus
        const buttons = Array.from(item.querySelectorAll('.quantity__button, button'));
        if (buttons.length >= 2 && buttons[0] === btn) next = Math.max(0, current - 1);
        else next = current + 1;
      } else {
        next = isMinus ? Math.max(0, current - 1) : current + 1;
      }

      qtyInput.value = String(next);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Schedule debounced cart update (reduces long tasks)
      scheduleCartChange(line, next);

      // Prevent theme’s own heavy handler from running multiple times (optional)
      // NOTE: If this breaks your theme’s qty behavior, remove the stopPropagation lines.
      e.stopPropagation();
    }, { passive: true });
  }

  // ---------- 4) Improve add-to-cart perceived responsiveness ----------
  function initAddToCartFeedback() {
    document.addEventListener('click', (e) => {
      const atc = closestAny(e.target, [
        'button[name="add"]',
        'button[type="submit"][name="add"]',
        '[data-add-to-cart]',
        '.product-form__submit',
        '.sq-sticky-add-to-cart'
      ]);
      if (!atc) return;

      // immediate visual feedback for INP
      atc.classList.add('is-loading');
      atc.setAttribute('aria-busy', 'true');
      atc.disabled = true;

      // release after short time; theme will typically re-enable on its own
      setTimeout(() => {
        atc.disabled = false;
        atc.removeAttribute('aria-busy');
        atc.classList.remove('is-loading');
      }, 900);
    }, { passive: true });
  }

  // Run
  initQtyDelegation();
  initAddToCartFeedback();
})();
// Säkerställ att add-to-cart inte skickar properties
document.addEventListener('submit', function (e) {
  const form = e.target;
  if (!form || !form.action || !form.action.includes('/cart/add')) return;

  // Ta bort alla properties inputs
  form.querySelectorAll('input[name^="properties["]').forEach(i => i.remove());
});
(() => {
  let atcLock = false;

  const lock = () => {
    if (atcLock) return true;
    atcLock = true;
    setTimeout(() => atcLock = false, 800);
    return false;
  };

  // Skydda fetch
  const _fetch = window.fetch;
  window.fetch = function(input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    if (url && url.includes('/cart/add')) {
      if (lock()) {
        console.warn('⛔ Blockerad dubbel add via fetch');
        return Promise.resolve(new Response(JSON.stringify({ blocked: true }), { status: 200 }));
      }
    }
    return _fetch.apply(this, arguments);
  };

  // Skydda XHR
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url) {
    this.__isAdd = url && String(url).includes('/cart/add');
    return _open.apply(this, arguments);
  };

  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function() {
    if (this.__isAdd && lock()) {
      console.warn('⛔ Blockerad dubbel add via XHR');
      return;
    }
    return _send.apply(this, arguments);
  };
})();
